import { useEffect, useState } from 'react';
import type { EstadoProducto, FacturacionApi, Productos } from '../api/types';
import { Alerta, ErrorApi, Guia, Pill } from './ui';

const ETIQUETA: Record<EstadoProducto, { texto: string; tono: 'ok' | 'pendiente' | 'error' }> = {
  SIN_CODIGO: { texto: 'Sin código', tono: 'error' },
  NO_EN_CATALOGO: { texto: 'Código no válido', tono: 'pendiente' },
  OK: { texto: 'Listo', tono: 'ok' },
};

export function ProductosSeccion({ api, idEmpresa }: { api: FacturacionApi; idEmpresa: number }) {
  const [datos, setDatos] = useState<Productos | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [filtro, setFiltro] = useState<EstadoProducto | ''>('');
  const [buscar, setBuscar] = useState('');
  const [buscarAplicado, setBuscarAplicado] = useState('');

  useEffect(() => {
    let vivo = true;
    setError(null);
    api
      .productos(idEmpresa, filtro, buscarAplicado)
      .then((d) => vivo && setDatos(d))
      .catch((e) => vivo && setError(e));
    return () => {
      vivo = false;
    };
  }, [api, idEmpresa, filtro, buscarAplicado]);

  return (
    <section className="seccion">
      <Guia
        id="productos"
        titulo="Cómo preparar sus productos"
        pasos={[
          <>
            Cada producto que vende debe tener su <b>código CPBS</b> (Codificación Panameña de Bienes y Servicios, 4 dígitos)
            asignado en el sistema donde administra sus productos.
          </>,
          <>Revise abajo los productos marcados <b>Sin código</b> o <b>Código no válido</b> y corríjalos en ese sistema.</>,
          <>
            La tasa de ITBMS que se informa a la DGI (0, 7, 10 o 15 %) se toma del catálogo según el código asignado: verifique
            que la columna ITBMS coincida con lo que cobra.
          </>,
          <>Vuelva a esta pantalla para confirmar que todos quedaron en <b>Listo</b>.</>,
        ]}
        nota="Si un producto que cobra ITBMS no tiene un código válido, sus ventas no se podrán facturar y aparecerán en Documentos → Facturas no cargadas."
      />

      <header className="seccion__cab">
        <div>
          <h2>Productos y código CPBS</h2>
          <p className="texto-sec">Revisión de solo lectura. Los productos se editan en su sistema de productos.</p>
        </div>
      </header>

      <ErrorApi error={error} />

      {datos && (
        <>
          <div className="contadores">
            <Contador valor={datos.total} texto="Productos" activo={filtro === ''} onClick={() => setFiltro('')} />
            <Contador valor={datos.sinCodigo} texto="Sin código" tono="error" activo={filtro === 'SIN_CODIGO'} onClick={() => setFiltro('SIN_CODIGO')} />
            <Contador valor={datos.noEnCatalogo} texto="Código no válido" tono="pendiente" activo={filtro === 'NO_EN_CATALOGO'} onClick={() => setFiltro('NO_EN_CATALOGO')} />
            <Contador valor={datos.listos} texto="Listos" tono="ok" activo={filtro === 'OK'} onClick={() => setFiltro('OK')} />
          </div>

          {datos.sinCodigo + datos.noEnCatalogo === 0 && datos.total > 0 && (
            <Alerta tipo="exito" titulo="Todos sus productos tienen un código CPBS válido." />
          )}

          <form
            className="filtros"
            onSubmit={(e) => {
              e.preventDefault();
              setBuscarAplicado(buscar.trim());
            }}
          >
            <input placeholder="Buscar por nombre o código de producto" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar producto" />
            <button className="btn btn--secundario" type="submit">Buscar</button>
          </form>

          {datos.items.length === 0 ? (
            <div className="vacio">No hay productos con este filtro.</div>
          ) : (
            <div className="tabla-scroll">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Código CPBS</th>
                    <th>Descripción en el catálogo</th>
                    <th className="num">ITBMS</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.items.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.descripcion ?? '—'} <span className="mono texto-sec">#{p.id}</span>
                      </td>
                      <td className="mono">{p.codigo ?? '—'}</td>
                      <td>{p.descripcionCpbs ?? '—'}</td>
                      <td className="num">{p.impuesto === null ? '—' : `${p.impuesto} %`}</td>
                      <td>
                        <Pill tono={ETIQUETA[p.estado].tono}>{ETIQUETA[p.estado].texto}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {datos.items.length >= 500 && <p className="texto-sec">Se muestran los primeros 500. Use el buscador para encontrar otros.</p>}
            </div>
          )}
        </>
      )}
      {!datos && !error && <div className="cargando">Cargando productos…</div>}
    </section>
  );
}

// El contador no tiene tono "neutro" (TS 4.9 no estrecha ESTADO[e].tono con una comparación en línea)
export const sinNeutro = (t: 'ok' | 'pendiente' | 'error' | 'neutro') => (t === 'neutro' ? undefined : t);

function Contador(p: { valor: number; texto: string; tono?: 'ok' | 'pendiente' | 'error'; activo: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`contador${p.tono ? ' contador--' + p.tono : ''}${p.activo ? ' activo' : ''}`} onClick={p.onClick} aria-pressed={p.activo}>
      <span className="contador__valor">{p.valor}</span>
      <span className="contador__texto">{p.texto}</span>
    </button>
  );
}

export { Contador };
